import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  X,
  ArrowLeft,
  Users,
  Heart,
  Brain,
  HeadphonesIcon,
  Loader2,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Logo from "@/components/Logo";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";

// -----------------------------------------------------------------------------
// Static content (ported from Lovable's Pricing.tsx)
// -----------------------------------------------------------------------------
const FAQS = [
  {
    q: "What's the difference between monthly and yearly billing?",
    a: "Yearly billing gives you 2 months free — you pay for 10 months and get 12. You can switch between monthly and yearly anytime from your account.",
  },
  {
    q: "What counts as a family profile?",
    a: "Each person you track — a child, a parent, or a dependent — is one profile. Caregivers you invite for shared access don't count toward your profile limit.",
  },
  {
    q: "Can I invite my partner or another caregiver?",
    a: "Shared caregiver access is included on Guardian Family and Guardian Plus. Free accounts can track one child but can't invite additional caregivers.",
  },
  {
    q: "What happens if I hit my profile limit?",
    a: "You'll keep full access to existing profiles. To add more, upgrade to Guardian Family (up to 10) or Guardian Plus (unlimited).",
  },
  {
    q: "Do you offer refunds?",
    a: "Yes — every paid plan includes a 30-day money-back guarantee. Cancel from your account and we'll refund the current billing period.",
  },
  {
    q: "Is my family's health data secure?",
    a: "Data is encrypted in transit and at rest, and only visible to you and caregivers you explicitly invite.",
  },
];

const COMPARISON = [
  {
    name: "Plan Fit",
    icon: Users,
    features: [
      {
        name: "Best for",
        free: "A solo caregiver trying GuardianNest",
        family: "Parents and growing families with multiple children",
        plus: "Caregivers who want AI insights and unlimited access",
      },
      {
        name: "Key difference",
        free: "Core health tracking at no cost",
        family: "Shared access + doctor reports",
        plus: "AI analysis + premium support",
      },
    ],
  },
  {
    name: "Family Management",
    icon: Users,
    features: [
      { name: "Family profiles", free: "Up to 2 profiles", family: "Up to 10", plus: "Unlimited" },
      { name: "Health record tracking", free: true, family: true, plus: true },
      { name: "Shared access / caregivers", free: false, family: true, plus: true },
    ],
  },
  {
    name: "Health Tracking",
    icon: Heart,
    features: [
      { name: "Appointment management", free: true, family: true, plus: true },
      { name: "Medication tracking", free: true, family: true, plus: true },
      { name: "Vaccination records", free: true, family: true, plus: true },
      { name: "Growth tracking", free: true, family: true, plus: true },
      { name: "Sick Kid Mode", free: true, family: true, plus: true },
    ],
  },
  {
    name: "AI & Reports",
    icon: Brain,
    features: [
      { name: "AI smart document upload", free: false, family: true, plus: true },
      { name: "AI symptom analysis", free: false, family: false, plus: true },
      { name: "Doctor report generation", free: false, family: true, plus: true },
      { name: "Advanced health analytics", free: false, family: false, plus: true },
    ],
  },
  {
    name: "Support & Extras",
    icon: HeadphonesIcon,
    features: [
      { name: "Community support", free: true, family: true, plus: true },
      { name: "Priority email support", free: false, family: true, plus: true },
      { name: "Premium priority support", free: false, family: false, plus: true },
      { name: "Early access to new features", free: false, family: false, plus: true },
    ],
  },
];

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
function planKey(slug) {
  if (!slug) return "free";
  const s = String(slug).toLowerCase();
  if (s.includes("plus")) return "plus";
  if (s.includes("family") || s.includes("premium")) return "family";
  return "free";
}

function centsToDollars(cents) {
  if (!cents) return 0;
  return (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
}

function FeatureValue({ value }) {
  if (typeof value === "string") {
    return <span className="text-sm text-foreground">{value}</span>;
  }
  return value ? (
    <Check className="mx-auto h-5 w-5 text-primary" aria-label="Included" />
  ) : (
    <X className="mx-auto h-5 w-5 text-muted-foreground/40" aria-label="Not included" />
  );
}

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function Pricing() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();

  const initialBilling = params.get("cycle") === "yearly" ? "yearly" : "monthly";
  const [billing, setBilling] = useState(initialBilling);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data, error: err } = await supabase
        .from("pricing_plans")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (!mounted) return;
      if (err) {
        setError("Couldn't load pricing right now. Please refresh.");
      } else {
        setPlans(data || []);
      }
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const orderedPlans = useMemo(() => {
    // Ensure order: free, family, plus (in case Supabase order isn't perfect)
    const priority = { free: 0, family: 1, plus: 2 };
    return [...plans].sort(
      (a, b) => (priority[planKey(a.slug)] ?? 9) - (priority[planKey(b.slug)] ?? 9)
    );
  }, [plans]);

  const handleCheckout = async (plan) => {
    setError("");
    const key = planKey(plan.slug);
    if (key === "free") {
      navigate("/auth?mode=signup");
      return;
    }
    if (!user || typeof user !== "object") {
      navigate(
        `/auth?mode=signup&next=/pricing&plan=${plan.slug}&cycle=${billing}`
      );
      return;
    }
    setCheckoutLoading(plan.slug);
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/checkout/session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            plan_slug: plan.slug,
            billing_cycle: billing,
            origin_url: window.location.origin,
            user_email: user.email,
            user_id: user.id,
          }),
        }
      );
      const data = await resp.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.detail || "Could not start checkout.");
      }
    } catch (e) {
      setError(e?.message || "Could not start checkout. Please try again.");
      setCheckoutLoading("");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      {/* Hero */}
      <div className="bg-gradient-hero grain text-primary-foreground">
        <div className="container mx-auto px-6 pb-16 pt-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-2">
              <Logo size="sm" variant="white" />
            </Link>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              data-testid="pricing-back"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-10 text-center"
          >
            <p className="mb-4 inline-block rounded-full border border-primary-foreground/30 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary-foreground/80">
              Pricing
            </p>
            <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl md:text-6xl">
              Plans that grow with your family
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-primary-foreground/80 sm:text-lg">
              Start free, upgrade when you're ready. Every plan keeps your family's health organized
              and secure.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Billing cycle toggle */}
      <div className="container mx-auto -mt-6 px-6">
        <div className="flex justify-center">
          <div
            role="tablist"
            aria-label="Billing cycle"
            className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1 shadow-sm"
          >
            <button
              role="tab"
              aria-selected={billing === "monthly"}
              onClick={() => setBilling("monthly")}
              data-testid="pricing-toggle-monthly"
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                billing === "monthly"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              role="tab"
              aria-selected={billing === "yearly"}
              onClick={() => setBilling("yearly")}
              data-testid="pricing-toggle-yearly"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                billing === "yearly"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Yearly
              <Badge className="bg-accent/20 px-1.5 py-0 text-[10px] text-accent-foreground">
                2 months free
              </Badge>
            </button>
          </div>
        </div>
      </div>

      {/* Plan cards */}
      <div className="container mx-auto mt-8 px-6">
        {error && (
          <div
            className="mx-auto mb-6 max-w-2xl rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            data-testid="pricing-error"
          >
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-3">
            {orderedPlans.map((plan, i) => {
              const key = planKey(plan.slug);
              const isFree = key === "free";
              const featured = plan.is_featured || key === "family";
              const priceCents =
                billing === "yearly" ? plan.price_yearly_cents : plan.price_monthly_cents;
              const priceLabel = isFree ? "Free" : `$${centsToDollars(priceCents)}`;
              const period = isFree
                ? ""
                : billing === "yearly"
                ? "/year"
                : "/month";
              const monthlyEquivalent =
                billing === "yearly" && plan.price_yearly_cents > 0
                  ? `≈ $${(plan.price_yearly_cents / 100 / 12).toFixed(2)}/mo billed annually`
                  : null;

              return (
                <motion.div
                  key={plan.id || plan.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.45 }}
                  data-testid={`pricing-card-${plan.slug}`}
                  className={`relative flex flex-col rounded-2xl border bg-card p-6 shadow-sm transition-all ${
                    featured
                      ? "border-primary/60 ring-1 ring-primary/40"
                      : "border-border hover:border-primary/30"
                  }`}
                >
                  {featured && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-xs font-semibold uppercase tracking-wider text-primary-foreground">
                      Most popular
                    </span>
                  )}
                  {key === "plus" && (
                    <Sparkles className="absolute right-6 top-6 h-5 w-5 text-accent" />
                  )}

                  <h2 className="font-heading text-2xl font-bold text-foreground">{plan.name}</h2>
                  {plan.tagline && (
                    <p className="mt-1 text-sm text-muted-foreground">{plan.tagline}</p>
                  )}

                  <div className="mt-5">
                    <span className="font-heading text-4xl font-extrabold text-foreground">
                      {priceLabel}
                    </span>
                    {period && (
                      <span className="ml-1 text-sm text-muted-foreground">{period}</span>
                    )}
                  </div>
                  {monthlyEquivalent && (
                    <p className="mt-1 text-xs text-muted-foreground">{monthlyEquivalent}</p>
                  )}

                  <ul className="mt-6 flex-1 space-y-3">
                    {(plan.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant={featured ? "default" : "outline"}
                    size="lg"
                    className="mt-8 w-full"
                    disabled={checkoutLoading === plan.slug}
                    onClick={() => handleCheckout(plan)}
                    data-testid={`pricing-cta-${plan.slug}`}
                  >
                    {checkoutLoading === plan.slug ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening checkout…
                      </>
                    ) : isFree ? (
                      "Start Free"
                    ) : (
                      `Choose ${plan.name}`
                    )}
                  </Button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Feature comparison table */}
      <section className="container mx-auto px-6 pt-24">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Compare every feature
          </h2>
          <p className="mt-3 text-muted-foreground">
            Everything Free, Guardian Family, and Guardian Plus include — side by side.
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-5xl overflow-hidden rounded-2xl border border-border bg-card">
          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              {/* Header row */}
              <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-4 border-b border-border bg-muted/30 px-4 py-4 text-sm font-semibold text-foreground sm:px-6">
                <div>Features</div>
                <div className="text-center">Free</div>
                <div className="text-center text-primary">Guardian Family</div>
                <div className="text-center">Guardian Plus</div>
              </div>
              {COMPARISON.map((cat) => (
                <div key={cat.name}>
                  <div className="flex items-center gap-2 border-b border-border/60 bg-muted/10 px-4 py-3 text-sm font-heading font-bold text-foreground sm:px-6">
                    <cat.icon className="h-4 w-4 text-primary" />
                    {cat.name}
                  </div>
                  {cat.features.map((row) => (
                    <div
                      key={row.name}
                      data-testid={`comparison-row-${row.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="grid grid-cols-[1.6fr_1fr_1fr_1fr] items-center gap-4 border-b border-border/40 px-4 py-3 text-sm last:border-b-0 sm:px-6"
                    >
                      <div className="text-muted-foreground">{row.name}</div>
                      <div className="text-center">
                        <FeatureValue value={row.free} />
                      </div>
                      <div className="text-center">
                        <FeatureValue value={row.family} />
                      </div>
                      <div className="text-center">
                        <FeatureValue value={row.plus} />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-6 py-24">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Pricing questions
          </h2>
          <Accordion
            type="single"
            collapsible
            className="mt-10"
            data-testid="pricing-faq"
          >
            {FAQS.map((f, i) => (
              <AccordionItem value={`faq-${i}`} key={f.q}>
                <AccordionTrigger className="text-left font-heading text-base font-semibold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10">
        <div className="container mx-auto flex flex-col items-center justify-between gap-6 px-6 md:flex-row">
          <Logo size="sm" />
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} GuardianNest. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/contact" className="hover:text-foreground">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
